import { CookieEventDetail, CookieSelectedEventDetail } from "../@types/cookies";
import { cp } from "../config";
import { ALL_COOKIE_TYPES, COOKIE_TYPES, CookieType } from "../enums/cookies";
import { gdprCookieHasValue, saveCookieSettingsAPI, setLocalCookie } from "../utils/cookieUtils";

export class CookieService {
    private static instance: CookieService;
    private consentModal: HTMLDialogElement | null = null;
    private prefsModal: HTMLDialogElement | null = null;

    private constructor() {
        this.addGlobalListeners();
    }

    public static getInstance(): CookieService {
        if (!CookieService.instance) {
            CookieService.instance = new CookieService();
        }
        return CookieService.instance;
    }

    public registerConsentModal(element: HTMLDialogElement | null): void {
        this.consentModal = element;
        this.consentModal?.focus();
    }

    public registerPrefsModal(element: HTMLDialogElement | null): void {
        this.prefsModal = element;
    }

    private addGlobalListeners(): void {
        document.addEventListener("cookies:saveDeclined", (event: Event) => {
            const customEvent = event as CustomEvent<CookieEventDetail>;
            const { actionButtons } = customEvent.detail;
            this.saveSelection([], actionButtons);
        });

        document.addEventListener("cookies:saveAll", (event: Event) => {
            const customEvent = event as CustomEvent<CookieEventDetail>;
            const { actionButtons } = customEvent.detail;
            this.saveSelection(ALL_COOKIE_TYPES, actionButtons);
        });

        document.addEventListener("cookies:saveSelected", (event: Event) => {
            const customEvent = event as CustomEvent<CookieSelectedEventDetail>;
            const { settings, actionButtons } = customEvent.detail;
            this.saveSelection(settings, actionButtons);
        });

        document.addEventListener("cookies:showPrefs", this.showPrefsModal);
    }

    public shouldShowConsentModal(): boolean {
        const hasGdprCookie = document.cookie.includes("GDPR=");
        const hasRequiredValue = gdprCookieHasValue("required");
        return !hasGdprCookie || !hasRequiredValue;
    }

    public checkAndShowConsentModal(): void {
        if (this.consentModal && this.shouldShowConsentModal()) {
            this.consentModal.showModal();
        }
    }

    private async saveSelection(optionalCookies: CookieType[], actionButtons?: HTMLButtonElement[]): Promise<void> {
        actionButtons?.forEach((btn) => {
            if (btn) this.setButtonLoading(btn, true);
        });

        const settingsToSave = [...optionalCookies, COOKIE_TYPES.REQUIRED];

        try {
            if (cp.cfg.ENVIRONMENT === "prod") {
                await saveCookieSettingsAPI(settingsToSave);
            } else {
                setLocalCookie(settingsToSave);
            }
        } catch (error) {
            console.error("Error saving cookie settings:", error);
        } finally {
            actionButtons?.forEach((btn) => {
                if (btn) this.setButtonLoading(btn, false);
            });

            this.consentModal?.close();
            this.prefsModal?.close();
            window.location.reload();
        }
    }

    private setButtonLoading(button: HTMLButtonElement, loading: boolean) {
        button.disabled = loading;
        button.setAttribute("aria-busy", String(loading));
    }

    private showPrefsModal = () => {
        this.consentModal?.close();
        this.prefsModal?.showModal();
    };

    public getActiveCookieTypes(): CookieType[] {
        return ALL_COOKIE_TYPES.filter((type) => gdprCookieHasValue(type));
    }
}

if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
            window.cookieService = CookieService.getInstance();
            document.dispatchEvent(new CustomEvent("cookies:serviceReady"));
        });
    } else {
        window.cookieService = CookieService.getInstance();
        document.dispatchEvent(new CustomEvent("cookies:serviceReady"));
    }
}
